import { Link } from 'react-router-dom'
import { Trash } from 'iconoir-react'
import { Binoculars } from '@phosphor-icons/react'
import moment from 'moment'
import { ReactElement, useMemo } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Button } from 'app/components/ui/button'
import { NavTabs } from 'app/components/ui/nav-tabs'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { PageScroll } from 'app/components/layout/PageScroll'
import { PageHeaderComponent } from 'app/components/page-header/PageHeaderComponent'
import { EmptySearch } from 'app/components/shared/styled/EmptySearch'
import SimpleTable from 'app/components/table/SimpleTable'
import type { InviteRow } from './useInvitedUsers'

export type InvitedUsersShadcnComponentProps = {
  invites: InviteRow[]
  canManageUsers: boolean
  canEditUser: boolean
  onDelete: (invite: InviteRow) => void
}

const tabs = [
  { name: `Active`, href: `/settings/users/active` },
  { name: `Invited`, href: `/settings/users/invited` },
]

export function InvitedUsersShadcnComponent({
  invites,
  canManageUsers,
  canEditUser,
  onDelete,
}: InvitedUsersShadcnComponentProps): ReactElement {
  const columns = useMemo(
    (): ColumnDef<InviteRow, any>[] => [
      {
        id: `email`,
        accessorKey: `email`,
        header: `Email`,
        cell: (props) => <span className="font-medium text-strong">{props.getValue()}</span>,
        meta: { defaultVisible: true },
      },
      {
        id: `roleName`,
        accessorKey: `roleName`,
        header: `Role`,
        meta: { defaultVisible: true },
      },
      {
        id: `createdAt`,
        accessorKey: `createdAt`,
        header: `Date Invited`,
        cell: (props) => <>{props.getValue() ? moment(props.getValue()).format(`MMMM Do YYYY`) : `-`}</>,
        meta: { defaultVisible: true },
      },
      ...(canManageUsers
        ? [
            {
              id: `actions`,
              header: `Actions`,
              size: 80,
              cell: (props: any) => (
                <TooltipWrapper message="Delete invitation">
                  <button onClick={() => onDelete(props.row.original)} className="text-minimal hover:text-red-500">
                    <Trash className="h-4 w-4" />
                  </button>
                </TooltipWrapper>
              ),
              meta: { defaultVisible: true },
            } as ColumnDef<InviteRow, any>,
          ]
        : []),
    ],
    [canManageUsers, onDelete],
  )

  return (
    <PageScroll
      variant="table"
      header={
        <>
          <PageHeaderComponent
            title="Users"
            primaryButtonSlot={
              canEditUser ? (
                <Link to="/settings/users/new">
                  <Button variant="primary" size="sm">
                    + Add User
                  </Button>
                </Link>
              ) : undefined
            }
          />

          <div className="mb-8">
            <NavTabs tabs={tabs} />
          </div>
        </>
      }
    >
      {invites.length === 0 ? (
        <div className="flex w-full items-center justify-center py-12">
          <EmptySearch
            icon={() => <Binoculars size={60} />}
            title="No Invited Users Yet"
            description="You haven't invited any users yet. Use the + Add User button to send an invite."
          />
        </div>
      ) : (
        <SimpleTable columns={columns} data={invites} hideFooter edgePadded />
      )}
    </PageScroll>
  )
}
